<template>
  <div>
    <booking-total-stay :price-total="quote.priceTotal" />
    <transition name="fade">
      <component
        :is="paymentComponent.component"
        v-if="paymentComponent"
        v-bind="paymentComponent.props"
        class="payments-due"
      />
    </transition>
    <section v-if="showImmediatePaymentOption" class="pay-all-now">
      <base-checkbox
        :model-value="takePaymentImmediately"
        @change="
          $emit('take-payment-immediately-changed', !takePaymentImmediately)
        "
      >
        <translate>Pay in one lump sum now</translate>
      </base-checkbox>
    </section>
    <arrival-taxes :taxes="quote.arrivalTaxes" />
  </div>
</template>

<script lang="ts">
import BookingPaymentDeferred from './booking-total-cost/BookingPaymentDeferred.vue'
import BookingPaymentImmediate from './booking-total-cost/BookingPaymentImmediate.vue'
import BookingPaymentPitchupOnly from './booking-total-cost/BookingPaymentPitchupOnly.vue'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { BookingInfo } from '../data/schema'
import { Quote } from '../quote/schema'

@Component
class BookingTotalCost extends Vue {
  @Prop()
  takePaymentImmediately: boolean

  @Prop()
  details: BookingInfo

  @Prop()
  quote: Quote

  get depositPercent(): number {
    return this.quote.commission
  }

  get components() {
    return {
      deferred_payment: {
        component: BookingPaymentDeferred,
        props: this.deferredPaymentProperties,
      },
      immediate_payment: {
        component: BookingPaymentImmediate,
        props: this.immediatePaymentProperties,
      },
      pitchup_only: {
        component: BookingPaymentPitchupOnly,
        props: this.pitchupOnlyProperties,
      },
    }
  }

  get paymentComponent() {
    return this.takePaymentImmediately
      ? this.components.immediate_payment
      : this.components[this.quote.paymentWhen]
  }

  get showImmediatePaymentOption(): boolean {
    return (
      this.quote.paymentWhen === 'deferred_payment' &&
      this.details.allowImmediatePayment
    )
  }

  get deferredPaymentProperties() {
    return {
      depositPercent: this.depositPercent,
      campsiteName: this.details.campsiteName,
      balanceDueDate: this.quote.balanceDueDate,
      deposit: this.quote.deposit,
      remainder: this.quote.remainder,
      refundUntilDate: this.quote.refundUntilDate,
    }
  }

  get immediatePaymentProperties() {
    return {
      campsiteName: this.details.campsiteName,
      depositPercent: this.depositPercent,
      priceTotal: this.quote.priceTotal,
      refundUntilDate: this.quote.refundUntilDate,
    }
  }

  get pitchupOnlyProperties() {
    return {
      campsiteName: this.details.campsiteName,
      deposit: this.quote.deposit,
      depositPercent: this.depositPercent,
      balanceDueDate: this.quote.balanceDueDate,
      remainder: this.quote.remainder,
      paymentTiming: this.details.paymentTiming,
      refundUntilDate: this.quote.refundUntilDate,
    }
  }
}

export default toNative(BookingTotalCost)
</script>
<style>
@import url('~/assets/css/payment-due.css');
</style>
<style scoped>
.payments-due :deep(tr td:last-of-type) {
  text-align: right;
}

.payments-due :deep(tr td[colspan='2']) {
  text-align: left;
  white-space: normal;
}

.payments-due :deep(tr.info) {
  font-size: var(--body);
  border-bottom: 1px solid var(--colour-page-background);
}

.payments-due :deep(.info td) {
  padding-top: 0;
}

.pay-all-now {
  background: var(--colour-pale-blue);
  border-radius: 0.5em;
  padding: 0.5em;
  margin: 1em 0;
}

.pay-all-now :deep(label) {
  margin: 0;
}

.pay-all-now :deep(.checkbox-tick),
.pay-all-now :deep(label:hover .checkbox-tick) {
  border: 1px solid var(--colour-mid-blue);
}
</style>
